/** @jsxImportSource @emotion/react */
import { formatEasternDateTime } from 'helpers/dates'
import React from 'react'

import { ChargeSection } from 'components/lab/billingCharge/BillingChargeUpsert'
import { BillingChargeLogEntry, BillingChargeSubmissionLogEntry } from 'store/types'

interface Props {
  submissionLog?: BillingChargeSubmissionLogEntry[]
  chargeLog?: BillingChargeLogEntry[]
}

// A COB claim is the same claim sent onward to the next payer, so the rank is
// what tells two log lines apart when they name different payers on the same
// charge.
const CLAIM_LABELS: { [key: string]: string } = {
  primary: 'Primary',
  secondary: 'Secondary (COB)',
  tertiary: 'Tertiary (COB)',
}

const METHOD_LABELS: { [key: string]: string } = {
  ELECTRONIC: 'Electronic',
  PAPER: 'Paper',
}

const POSITION_LABELS: { [key: number]: string } = {
  2: 'secondary',
  3: 'tertiary',
}

// The two logs rendered as one history: claims that went out, and changes a
// remittance made to the charge.
type HistoryRow = {
  key: string
  at: string
  event: string
  payer: string
  user: string
  details: string
}

const submissionRow = (entry: BillingChargeSubmissionLogEntry, index: number): HistoryRow => ({
  key: `submission-${entry.submittedAt}-${entry.payerRank}-${index}`,
  at: entry.submittedAt,
  event: `${CLAIM_LABELS[entry.payerRank] ?? entry.payerRank} claim submitted`,
  payer: entry.payerName || (entry.payerId ? `#${entry.payerId}` : '—'),
  user: entry.userName || '—',
  details: METHOD_LABELS[entry.method] ?? entry.method,
})

const chargeLogRow = (entry: BillingChargeLogEntry, index: number): HistoryRow => {
  const remittance = [
    entry.payingPayerName ? `forwarded by ${entry.payingPayerName}` : 'forwarded',
    entry.crossoverCarrierName ? `as "${entry.crossoverCarrierName}"` : null,
    entry.crossoverPayerId ? `(id ${entry.crossoverPayerId})` : null,
    entry.eraFilename ? `· ${entry.eraFilename}` : null,
    entry.checkNumber ? `· check ${entry.checkNumber}` : null,
  ]
    .filter(Boolean)
    .join(' ')

  return {
    key: `charge-${entry.at}-${entry.kind}-${index}`,
    at: entry.at,
    event: `Added as ${POSITION_LABELS[entry.position] ?? `payer ${entry.position}`} from remittance`,
    payer: entry.payerName || `#${entry.payerId}`,
    user: entry.userName || (entry.userId ? `#${entry.userId}` : 'System'),
    details: remittance,
  }
}

/**
 * The charge's history, newest first: claim submissions and the changes a
 * remittance made to the charge. Reference material rather than part of the
 * form, so the section starts collapsed.
 */
export const ChargeLogsSection = ({ submissionLog = [], chargeLog = [] }: Props) => {
  const entries = [...submissionLog.map(submissionRow), ...chargeLog.map(chargeLogRow)].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  )

  return (
    <ChargeSection
      title={`Logs${entries.length ? ` (${entries.length})` : ''}`}
      defaultOpen={false}
    >
      {entries.length === 0 ? (
        <div className="text-muted font-italic">Nothing has happened to this charge yet.</div>
      ) : (
        <div css={{ overflowX: 'auto' }}>
          <table className="table table-sm mb-0" css={{ background: '#fff' }}>
            <thead>
              <tr>
                <th css={{ whiteSpace: 'nowrap' }}>Date</th>
                <th>Event</th>
                <th>Payer</th>
                <th>User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.key}>
                  <td css={{ whiteSpace: 'nowrap' }}>
                    {formatEasternDateTime(entry.at)}
                  </td>
                  <td>{entry.event}</td>
                  <td>{entry.payer}</td>
                  <td>{entry.user}</td>
                  <td>{entry.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ChargeSection>
  )
}
